import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component, EventEmitter, Input, OnChanges,
  OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { Property, PropertyType } from '../../shared/models';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SpUtilities } from '../../../../shared/services/utilities';
import { CustomValidators } from '../../../../shared/validators';
import { ApiHelperService } from '../../../../shared/services/api';
import { UserProfile } from '../../../user/models';
import { ValueChangedEvent } from 'devextreme/ui/file_uploader';
import { DxFileUploaderComponent } from 'devextreme-angular';

@Component({
  selector: 'sp-property-details-edit',
  templateUrl: './property-details-edit.component.html',
  styleUrls: ['./property-details-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class PropertyDetailsEditComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() property: Property;
  @Input() propertyTypes: PropertyType[];
  @Input() userProfile: UserProfile;
  @Input() saveError: string | null;
  @Output() save = new EventEmitter<Property>();
  @Output() saveImage = new EventEmitter<string>();
  @Output() removeImage = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();
  @ViewChild('dxPhotoFileUpload', { static: false }) dxPhotoFileUpload: DxFileUploaderComponent;

  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  currentYear: number;
  feedbackMessages: any[] = [];
  image: any;
  img: string;
  profileImage: string;
  states: Array<string>;
  errorMessage: string;

  constructor(private fb: UntypedFormBuilder,
    private apiHelper: ApiHelperService,
    private utils: SpUtilities) {
    this.currentYear = new Date().getFullYear();
  }

  ngAfterViewInit(): void {

    const token = window.localStorage.getItem('access_token');

    const uploadHeaders = {
      Authorization: `Bearer ${token}`
    };

    if (this.dxPhotoFileUpload?.instance) {
      this.dxPhotoFileUpload.instance.option('uploadHeaders', uploadHeaders);
      this.dxPhotoFileUpload.instance.option({
        maxFileSize: 5000000, //5MB
        allowedFileExtensions: ['.jpg', '.jpeg', '.png'],
        accept: 'image/*',
        uploadUrl: `${this.apiHelper.apiUrl}/api/property/${this.property.propertyId}/profileimage`,
        uploadHeaders: uploadHeaders
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['property']) {
      if (this.property) {
        this.property.yearOfConversion = this.property.yearOfConversion === 0 ? null : this.property.yearOfConversion;
        const isCondoConverted = this.property.isCondoConverted;
        this.setYearOfConversionValidators(isCondoConverted, this.property.yearBuilt);

        // Update form controls with the new property values
        this.formGroup.patchValue({
          propertyName: this.property.name,
          address1: this.property.addressLine1,
          address2: this.property.addressLine2,
          city: this.property.city,
          state: this.property.state,
          zipCode: this.property.zipCode,
          yearBuilt: this.property.yearBuilt,
          propertyType: this.property.propertyTypeId,
          numberOfUnits: this.property.numberOfUnits,
          numberOfFloors: this.property.numberOfFloors,
          isCondoConverted: this.property.isCondoConverted,
          yearOfConversion: this.property.yearOfConversion,
          propertyDescription: this.property.propertyDescription,
          isFeatureToggleOn: this.property.isFeatureToggleOn,
          isTest: this.property.isTest,
          allowPlanning: this.property.allowPlanning
        });
      }

      if (this.property && this.property.profileImage) {
        this.profileImage = this.ignoreCache(this.property.profileImage);
      }
    }
  }

  ngOnInit() {
    this.states = this.utils.getStates();
    this.initForm();
  }

  checkDisabled() {
    const yearBuilt = this.formGroup.get('yearBuilt').value;
    const isCondoConverted = this.formGroup.get('isCondoConverted').value;
    const yearOfConversion = this.formGroup.get('yearOfConversion').value;
    const numberOfUnits = this.formGroup.get('numberOfUnits').value;

    return !this.formGroup.valid
      || yearBuilt < 1700
      || yearBuilt > this.currentYear
      || (isCondoConverted && (yearOfConversion < yearBuilt || yearOfConversion > this.currentYear))
      || numberOfUnits < 0
      || numberOfUnits > 99999;
  }

  onDxUploaded() {
    this.saveImage.emit(this.property.propertyId);
  }

  onDxValueChanged(e: ValueChangedEvent) {
    const file = e.value[0];

    const maxFileSize = this.dxPhotoFileUpload.instance.option('maxFileSize');
    const allowedFileExtensions = this.dxPhotoFileUpload.instance.option('allowedFileExtensions');

    const fileExt = `.${file.name.split('.').pop()}`; //Get the file extension      
    if (!allowedFileExtensions.includes(fileExt)) {
      this.errorMessage = `Invalid file type. Allowed types: ${allowedFileExtensions.join(', ')}.`;
    } else if (file.size > maxFileSize) {
      this.errorMessage = 'File is too large.';
    } else {
      this.errorMessage = null;
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onIsCondoConverted(isCondoConverted) {
    this.property.isCondoConverted = isCondoConverted;

    if (!isCondoConverted) {
      this.property.yearOfConversion = null;
      this.formGroup.get('yearOfConversion').reset();
    }

    this.setYearOfConversionValidators(isCondoConverted);
  }

  onSave() {

    this.property.name = this.formGroup.value.propertyName.trim();
    this.property.addressLine1 = this.formGroup.value.address1.trim();
    this.property.addressLine2 = this.formGroup.value.address2 ? this.formGroup.value.address2.trim() : '';
    this.property.city = this.formGroup.value.city.trim();
    this.property.state = this.formGroup.value.state;
    this.property.zipCode = this.formGroup.value.zipCode.trim();
    this.property.yearBuilt = this.formGroup.value.yearBuilt.toString().trim();
    this.property.propertyTypeId = this.formGroup.value.propertyType;
    this.property.numberOfUnits = this.formGroup.value.numberOfUnits.toString().trim();
    this.property.numberOfFloors = this.formGroup.value.numberOfFloors.toString().trim();
    this.property.propertyDescription = this.formGroup.value.propertyDescription
      ? this.formGroup.value.propertyDescription.trim()
      : '';
    this.property.isCondoConverted = this.formGroup.value.isCondoConverted;
    this.property.yearOfConversion = this.formGroup.value.yearOfConversion
      ? this.formGroup.value.yearOfConversion.toString().trim()
      : 0;

    this.property.isFeatureToggleOn = this.formGroup.value.isFeatureToggleOn;
    this.property.isTest = this.formGroup.value.isTest;
    this.property.allowPlanning = this.formGroup.value.allowPlanning;

    this.save.emit(this.property);
  }

  onRemoveProfileImage() {
    this.removeImage.next(this.property.propertyId);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onYearBuildChanged(e) {
    const yearBuilt = this.formGroup.get('yearBuilt').value;
    this.setYearOfConversionValidators(false, yearBuilt);
  }

  private ignoreCache(url) {
    return url + '?rand=' + Math.random();
  }

  private initForm() {
    this.property.yearOfConversion = this.property.yearOfConversion === 0 ? null : this.property.yearOfConversion;
    const isCondoConverted = this.property.isCondoConverted;
    this.setYearOfConversionValidators(isCondoConverted, this.property.yearBuilt);

    this.formGroup.addControl('propertyName', this.fb.control(this.property.name, Validators.compose([
      Validators.required,
      CustomValidators.allowedCharsStringValidator2,
      CustomValidators.notEmptyStringValidator,
    ])));
    this.formGroup.addControl('address1', this.fb.control(this.property.addressLine1, Validators.compose([
      Validators.required,
      CustomValidators.allowedCharsStringValidator2,
      CustomValidators.notEmptyStringValidator,
    ])));
    this.formGroup.addControl('address2', this.fb.control(this.property.addressLine2, Validators.compose([
      CustomValidators.allowedCharsStringValidator2,
      CustomValidators.notEmptyStringValidator,
    ])));
    this.formGroup.addControl('city', this.fb.control(this.property.city, Validators.compose([
      Validators.required,
      CustomValidators.allowedCharsStringValidator2,
      CustomValidators.notEmptyStringValidator,
    ])));
    this.formGroup.addControl('state', this.fb.control(this.property.state, Validators.compose([
      Validators.required
    ])));
    this.formGroup.addControl('zipCode', this.fb.control(this.property.zipCode, Validators.compose([
      Validators.required,
      CustomValidators.zipcodeValidator
    ])));
    this.formGroup.addControl('yearBuilt', this.fb.control(this.property.yearBuilt, Validators.compose([
      Validators.required,
      CustomValidators.yearValidator,
      CustomValidators.rangeValidator(1700, this.currentYear, (min, max) => `Property year built should be between ${min} and ${max}`)
    ])));
    this.formGroup.addControl('propertyType', this.fb.control(this.property.propertyTypeId, Validators.compose([
      Validators.required
    ])));
    this.formGroup.addControl('numberOfUnits', this.fb.control(this.property.numberOfUnits, Validators.compose([
      Validators.required,
      CustomValidators.noDecimalPointNumber,
      CustomValidators.positiveNumber,
      // CustomValidators.numberInDigitsValidator,
      CustomValidators.rangeValidator(1, 99999, (min, max) => `Number of units should be between ${min} and ${max}.`)
    ])));
    this.formGroup.addControl('numberOfFloors', this.fb.control(this.property.numberOfFloors));
    this.formGroup.addControl('isCondoConverted', this.fb.control(this.property.isCondoConverted));
    this.formGroup.addControl('yearOfConversion', this.fb.control(this.property.yearOfConversion));
    this.formGroup.addControl('propertyDescription', this.fb.control(this.property.propertyDescription, Validators.compose([
      CustomValidators.notEmptyStringValidator,
    ])));

    this.formGroup.addControl('fileUpload', this.fb.control(''));

    this.formGroup.addControl('isFeatureToggleOn', this.fb.control(this.property.isFeatureToggleOn));
    this.formGroup.addControl('isTest', this.fb.control(this.property.isTest));
    this.formGroup.addControl('allowPlanning', this.fb.control(this.property.allowPlanning));
  }

  private setYearOfConversionValidators(required: boolean, yearBuilt?: number) {

    const yearOfConversionControl = this.formGroup.get('yearOfConversion');
    const yearBuiltControl = this.formGroup.get('yearBuilt');
    if (yearOfConversionControl == null) { return; }

    yearOfConversionControl.clearValidators();

    if (!yearBuilt) {
      yearBuilt = yearBuiltControl.value;
    }

    if (required) {

      if (yearBuiltControl.valid) {

        let validationMessage = '';

        if (Number(yearBuilt) === this.currentYear) {
          validationMessage = `Year of Conversion must be ${this.currentYear}.`;
        } else {
          validationMessage = `Year of Conversion must be between ${yearBuilt} and ${this.currentYear}.`;
        }

        yearOfConversionControl.setValidators((Validators.compose([
          Validators.required,
          CustomValidators.yearValidator,
          CustomValidators.rangeValidator(yearBuilt, this.currentYear,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            (min, max) => validationMessage)
        ])));
      } else {
        yearOfConversionControl.setValidators((Validators.compose([
          Validators.required
        ])));
      }

      // Validate Conversion Year
      if (yearOfConversionControl.value == null) {
        yearOfConversionControl.updateValueAndValidity();
        yearOfConversionControl.markAsPristine();
        yearOfConversionControl.markAsUntouched();
      } else {
        yearOfConversionControl.updateValueAndValidity();
        yearOfConversionControl.markAsDirty();
        yearOfConversionControl.markAsTouched();
      }
    }
  }
}
